import { useEffect, useRef, useState } from "react";
import { batchProcessPayrolls, fetchPayrollHub, getPayroll, newIdempotencyKey } from "./payrollApi";
import { formatPayrollRunLabel } from "../../components/common/PayrollRunSelect";
import { readPayrollWorkflow } from "./payrollWorkflow";

function isTerminal(executionStatus, runStatus) {
  if (["completed", "failed", "partial_success"].includes(executionStatus || "")) return true;
  if (runStatus === "processed") return true;
  return false;
}

function StatusBadge({ executionStatus }) {
  const es = (executionStatus || "draft").toLowerCase();
  const map = {
    queued: "bg-yellow-100 text-yellow-900 border-yellow-300",
    running: "bg-blue-100 text-blue-900 border-blue-300",
    completed: "bg-green-100 text-green-900 border-green-300",
    failed: "bg-red-100 text-red-900 border-red-300",
    partial_success: "bg-orange-100 text-orange-900 border-orange-300",
    error: "bg-red-100 text-red-900 border-red-300",
    draft: "bg-gray-100 text-gray-800 border-gray-300",
  };
  const cls = map[es] || map.draft;
  return (
    <span className={`inline-flex px-2 py-0.5 rounded border text-xs font-medium ${cls}`}>
      {executionStatus || "—"}
    </span>
  );
}

export default function PayrollBatchProcess() {
  const [selectedRunIds, setSelectedRunIds] = useState([]);
  const [runOptions, setRunOptions] = useState([]);
  const [shadowLegacy, setShadowLegacy] = useState(false);
  const [parallelism, setParallelism] = useState("");

  const [submitError, setSubmitError] = useState(null);
  const [batchMeta, setBatchMeta] = useState({ idempotencyKey: null, celery_task_ids: [] });
  const [rows, setRows] = useState([]);
  const [polling, setPolling] = useState(false);

  const pollRef = useRef(null);
  const taskIdsRef = useRef([]);

  useEffect(() => {
    fetchPayrollHub()
      .then((res) => {
        const opts = [];
        for (const period of res.data?.periods || []) {
          for (const run of period.runs || []) {
            opts.push({
              id: run.payroll_run_id,
              label: formatPayrollRunLabel(run, period),
            });
          }
        }
        setRunOptions(opts);
      })
      .catch(() => setRunOptions([]));

    const wf = readPayrollWorkflow();
    if (wf.payrollRunId) {
      setSelectedRunIds((current) =>
        current.length ? current : [wf.payrollRunId]
      );
    }
  }, []);

  useEffect(() => {
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, []);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setPolling(false);
  };

  const refreshAll = async (ids, celeryTaskIds = []) => {
    const next = await Promise.all(
      ids.map(async (rid, i) => {
        try {
          const { data } = await getPayroll(rid);
          return {
            payroll_run_id: rid,
            execution_status: data.execution_status,
            status: data.status,
            processed_at: data.processed_at,
            celery_task_id: celeryTaskIds[i] ?? null,
          };
        } catch (e) {
          return {
            payroll_run_id: rid,
            execution_status: "error",
            status: null,
            processed_at: null,
            celery_task_id: celeryTaskIds[i] ?? null,
            error:
              (typeof e?.response?.data?.detail === "string"
                ? e.response.data.detail
                : null) || e.message,
          };
        }
      })
    );
    setRows(next);
    return next;
  };

  const startPolling = (ids) => {
    stopPolling();
    setPolling(true);
    const taskIds = taskIdsRef.current;
    const tick = async () => {
      const snapshot = await refreshAll(ids, taskIds);
      const allDone = snapshot.every((r) =>
        isTerminal(r.execution_status, r.status)
      );
      if (allDone) stopPolling();
    };
    tick();
    pollRef.current = setInterval(tick, 3000);
  };

  const toggleRun = (runId) => {
    setSelectedRunIds((current) =>
      current.includes(runId)
        ? current.filter((id) => id !== runId)
        : [...current, runId]
    );
  };

  const runBatch = async () => {
    const ids = selectedRunIds;
    if (ids.length === 0) {
      alert("Select at least one payroll run");
      return;
    }

    setSubmitError(null);
    const idempo = newIdempotencyKey();

    try {
      const p =
        parallelism === "" ? undefined : Number(parallelism);
      const res = await batchProcessPayrolls(ids, {
        idempotencyKey: idempo,
        shadowLegacy,
        parallelism: Number.isFinite(p) ? p : undefined,
      });

      const taskIds = res.data?.celery_task_ids || [];
      taskIdsRef.current = taskIds;
      setBatchMeta({
        idempotencyKey: res.idempotencyKey,
        celery_task_ids: taskIds,
      });

      if (res.status === 202) {
        await refreshAll(ids, taskIds);
        startPolling(ids);
        return;
      }

      if (res.status === 200) {
        await refreshAll(ids, []);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setSubmitError(typeof detail === "string" ? detail : JSON.stringify(detail || e.message));
    }
  };

  const doneCount = rows.filter((r) => isTerminal(r.execution_status, r.status)).length;

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">Batch process payroll</h1>
          <p className="text-gray-500 text-sm">
            POST /payrolls/batch/process — multiple runs with async status when Celery is enabled.
          </p>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg p-6 max-w-4xl space-y-4">
        <div>
          <label className="block text-sm text-gray-600 mb-2">Payroll runs</label>
          {runOptions.length === 0 ? (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
              No payroll runs found. Create pay periods and runs first.
            </p>
          ) : (
            <div className="max-h-56 overflow-y-auto border rounded-md divide-y">
              {runOptions.map((opt) => (
                <label
                  key={opt.id}
                  className="flex items-start gap-3 p-3 hover:bg-slate-50 cursor-pointer text-sm"
                >
                  <input
                    type="checkbox"
                    className="mt-1 rounded border-gray-300"
                    checked={selectedRunIds.includes(opt.id)}
                    onChange={() => toggleRun(opt.id)}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
          <input
            type="checkbox"
            checked={shadowLegacy}
            onChange={(e) => setShadowLegacy(e.target.checked)}
            className="rounded border-gray-300"
          />
          Compare with legacy payroll (shadow mode)
        </label>

        <div>
          <label className="block text-sm text-gray-600 mb-1">Parallelism (optional)</label>
          <input
            type="number"
            min={1}
            max={64}
            value={parallelism}
            onChange={(e) => setParallelism(e.target.value)}
            className="border rounded-md p-2 w-32"
          />
        </div>

        {submitError && (
          <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
            {submitError}
          </div>
        )}

        <div className="border rounded-lg p-3 bg-gray-50 text-sm space-y-1">
          <p>
            <span className="text-gray-500">Last Idempotency-Key:</span>{" "}
            <span className="font-mono text-xs break-all">{batchMeta.idempotencyKey || "—"}</span>
          </p>
          {polling && <p className="text-blue-700 text-xs">Polling each run every 3s…</p>}
          {rows.length > 0 && (
            <p className="text-gray-700">
              Progress: {doneCount} / {rows.length} terminal
            </p>
          )}
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={runBatch}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-md font-medium"
          >
            Start batch
          </button>
        </div>

        {rows.length > 0 && (
          <div className="border rounded-lg overflow-hidden">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100 text-left">
                <tr>
                  <th className="p-2">Payroll run</th>
                  <th className="p-2">Status</th>
                  <th className="p-2">Run status</th>
                  <th className="p-2">Processed at</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.payroll_run_id} className="border-t">
                    <td className="p-2 text-xs">
                      {runOptions.find((o) => String(o.id) === String(r.payroll_run_id))?.label ||
                        "Payroll Run"}
                    </td>
                    <td className="p-2">
                      {r.error ? (
                        <span className="text-amber-800 text-xs">{r.error}</span>
                      ) : (
                        <StatusBadge executionStatus={r.execution_status} />
                      )}
                    </td>
                    <td className="p-2 text-xs">{r.status ?? "—"}</td>
                    <td className="p-2 text-xs">{r.processed_at ? String(r.processed_at) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
